// Physician Request — one template, three tick boxes (PPOT / LMN / Medical
// Records). The coordinator picks who it goes to (PCP, ordering physician, or
// another provider), ticks what they need, and the letter regenerates live.
// "Send by fax" goes through the send-fax edge function (Faxage). When PPOT
// is requested the blank DCH Appendix T shipped with the app goes along
// automatically (unless a pre-filled one is ticked from the client's Drive
// folder instead); any other Drive files ticked ride along too. The letter
// can also be downloaded or just filed to Drive without sending.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, addDays, subMonths } from 'date-fns';
import { supabase, updateClient, addDocument, addActivityLog } from '../../lib/supabase';
import { CLIENT_DRIVE_ROOT } from '../../lib/driveRoots';
import { sendFax } from '../../lib/faxes';
import FaxRecipientPicker from '../faxes/FaxRecipientPicker';
import { fmtPhone } from '../../lib/directory';
import useIsMobile from '../../hooks/useIsMobile';
import useFaxDraft, { draftStatusText } from '../../hooks/useFaxDraft';
import {
  renderPhysicianRequestBlob, blobToBase64, requestTitle, requestFileName,
  REQUEST_ITEMS, RECIPIENT_KINDS,
} from '../../lib/documents/physicianRequestPdf';

const FAXABLE = /\.(pdf|docx?|jpe?g|png|tiff?|txt|rtf)$/i;
const looksLikeAppendixT = (name) => /appendix\s*t|ppot|plan of treatment/i.test(name || '');
const looksLikeRelease = (name) => /release|authorization to disclose|roi\b/i.test(name || '');

// The blank DCH form, served with the app (same file the packet builder fills).
const BLANK_APPENDIX_T = { path: '/forms/gapp/appendix_t.pdf', name: 'GAPP Appendix T - Physician Plan of Treatment (blank).pdf' };
const BASE = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
async function loadBlankAppendixT() {
  const res = await fetch(`${BASE}${BLANK_APPENDIX_T.path}`);
  if (!res.ok) throw new Error('Could not load the blank Appendix T');
  return { name: BLANK_APPENDIX_T.name, base64: await blobToBase64(await res.blob()) };
}

const today = () => format(new Date(), 'yyyy-MM-dd');
const digits = (s) => String(s || '').replace(/\D/g, '');

function recipientFromClient(client, kind) {
  if (kind === 'pcp') return {
    name: client.doctor_name || '', practice: client.doctor_practice || '',
    fax: client.doctor_fax || '', phone: client.doctor_phone || '',
  };
  if (kind === 'ordering') return {
    name: client.ordering_physician_name || client.specialist_name || '',
    practice: client.ordering_physician_practice || client.specialist_practice || '',
    fax: client.ordering_physician_fax || client.specialist_fax || '',
    phone: client.ordering_physician_phone || client.specialist_phone || '',
  };
  return { name: '', practice: '', fax: '', phone: '' };
}

export default function PhysicianRequestModal({ client, profile, draft = null, onClose, onFiled, onSent, driveFiles = [] }) {
  const isMobile = useIsMobile();
  const saved = draft?.state || {};
  const [kind, setKind] = useState(saved.kind || 'other');
  const [recipient, setRecipient] = useState(saved.recipient || { name: '', practice: '', fax: '', phone: '' });
  // Never restored from a draft: a hand-typed number is confirmed afresh.
  const [faxConfirm, setFaxConfirm] = useState('');
  const [items, setItems] = useState(saved.items || { ppot: true, lmn: true, records: true });
  const [form, setForm] = useState(saved.form || {
    purpose: 'establish',
    recordsFrom: format(subMonths(new Date(), 12), 'yyyy-MM-dd'),
    recordsTo: today(),
    hoursSkilled: '',
    hoursUnskilled: '',
    neededBy: format(addDays(new Date(), 14), 'yyyy-MM-dd'),
    releaseEnclosed: false,
    notes: '',
    senderTitle: 'Care Coordinator',
    senderPhone: '',
  });
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  // Files from the client's Drive folder that can ride along with the letter.
  const faxable = useMemo(() => driveFiles.filter(f => FAXABLE.test(f.name) && !String(f.mimeType || '').includes('folder')), [driveFiles]);
  const [attached, setAttached] = useState(() => new Set(saved.attached || []));
  const toggleAttach = (id) => setAttached(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const attachedFiles = faxable.filter(f => attached.has(f.id));
  const prefilledAppendix = attachedFiles.some(f => looksLikeAppendixT(f.name));
  const [blankAppendix, setBlankAppendix] = useState(saved.blankAppendix ?? true);   // enclose the blank form when PPOT is requested
  const appendixAttached = items.ppot && (prefilledAppendix || blankAppendix);
  const releaseAttached = attachedFiles.some(f => looksLikeRelease(f.name));
  useEffect(() => { if (releaseAttached) setForm(p => p.releaseEnclosed ? p : { ...p, releaseEnclosed: true }); }, [releaseAttached]);
  const setR = (k) => (e) => { setRecipient(p => ({ ...p, [k]: e.target.value })); setDupWarning(''); setChosen(null); };

  // Who the letter goes to: the child's care team first, then anyone in the
  // Provider Directory, then typed by hand. `kind` still records whether that
  // is the PCP, the ordering physician or someone else, for the letter and
  // the activity log.
  const [chosen, setChosen] = useState(saved.chosen || null);
  // A records request goes to the records fax when the directory has one for
  // this doctor or site (directory_fax_routes) — for CHOA, Health Information
  // Services. The coordinator can send to the doctor's own fax instead, since
  // the PPOT and LMN need the doctor's signature.
  const [preferOwnFax, setPreferOwnFax] = useState(!!saved.preferOwnFax);
  const routedFax = (c) => (items.records && c?.recordsFax && !preferOwnFax) ? c.recordsFax : (c?.fax || '');
  const pickRecipient = (r) => {
    const c = { ...r.source, name: r.name, practice: r.practice, fax: r.fax, recordsFax: r.recordsFax || '', organisation: r.organisation || '', role: r.source.role, kind: r.kind };
    setChosen(c);
    setPreferOwnFax(false);
    setKind(r.kind);
    const fax = (items.records && c.recordsFax) ? c.recordsFax : c.fax;
    setRecipient({ name: r.name, practice: r.practice || '', fax: fax ? fmtPhone(fax) : '', phone: r.phone ? fmtPhone(r.phone) : '' });
    setFaxConfirm(''); setDupWarning('');
  };
  // Ticking or unticking Medical Records moves a picked recipient between
  // their fax and the records fax. A hand-typed number clears `chosen`, so it
  // is never touched.
  useEffect(() => {
    if (!chosen) return;
    const want = routedFax(chosen);
    setRecipient(p => digits(p.fax) === digits(want) ? p : { ...p, fax: want ? fmtPhone(want) : '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, items.records, preferOwnFax]);
  const clearRecipient = () => {
    setChosen(null); setKind('other'); setPreferOwnFax(false);
    setRecipient({ name: '', practice: '', fax: '', phone: '' });
    setFaxConfirm('');
  };

  const data = useMemo(() => ({
    client, recipient, items,
    records: { from: form.recordsFrom, to: form.recordsTo },
    hours: { skilled: form.hoursSkilled, unskilled: form.hoursUnskilled },
    purpose: form.purpose,
    neededBy: form.neededBy,
    releaseEnclosed: form.releaseEnclosed,
    ppotEnclosed: appendixAttached,
    ppotPrefilled: prefilledAppendix,
    notes: form.notes,
    sender: { name: profile?.full_name || '', title: form.senderTitle, phone: form.senderPhone },
    date: today(),
  }), [client, recipient, items, form, profile, appendixAttached, prefilledAppendix]);

  // Live preview — debounced, same renderer as the filed PDF.
  const [url, setUrl] = useState('');
  const [rendering, setRendering] = useState(true);
  const [previewErr, setPreviewErr] = useState('');
  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    setRendering(true); setPreviewErr('');
    const t = setTimeout(async () => {
      try {
        const blob = await renderPhysicianRequestBlob(data);
        if (mine !== seq.current) return;
        const next = URL.createObjectURL(blob);
        setUrl(prev => { if (prev) URL.revokeObjectURL(prev); return next; });
      } catch (e) {
        if (mine === seq.current) setPreviewErr(e.message || String(e));
      } finally {
        if (mine === seq.current) setRendering(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [data]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const nothingPicked = !Object.values(items).some(Boolean);
  const faxDigits = digits(recipient.fax);
  const faxValid = faxDigits.length === 10;
  // A number typed by hand (not pulled from the client record) must be typed
  // twice — a misdirected fax is the classic PHI breach.
  const faxFromRecord = faxDigits && (faxDigits === digits(recipientFromClient(client, kind).fax) || (chosen && (faxDigits === digits(chosen.fax) || faxDigits === digits(chosen.recordsFax))));
  const faxConfirmed = faxFromRecord || digits(faxConfirm) === faxDigits;

  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  // ── Draft ─────────────────────────────────────────────────────────────
  // Saves itself while you type; only once there is something beyond the
  // defaults (every item ticked, today's dates) worth keeping.
  const draftCtl = useFaxDraft({
    kind: 'request',
    clientId: client.id,
    initial: draft,
    profile,
    meaningful: !!(chosen || recipient.name.trim() || digits(recipient.fax) || form.notes.trim() || String(form.hoursSkilled || '').trim() || String(form.hoursUnskilled || '').trim() || attached.size || String(form.senderPhone || '').trim()),
    fields: {
      title: requestTitle(items),
      recipient_name: recipient.name.trim() || null,
      recipient_fax: digits(recipient.fax) || null,
      state: { kind, recipient, chosen, preferOwnFax, items, form, attached: [...attached], blankAppendix },
      staged_files: [],
    },
  });
  // Every way out saves first.
  const close = useCallback(async () => { if (busy) return; await draftCtl.flush(); onClose(); }, [busy, draftCtl, onClose]);
  const [discardArmed, setDiscardArmed] = useState(false);
  const discard = async () => {
    if (!discardArmed) { setDiscardArmed(true); setTimeout(() => setDiscardArmed(false), 5000); return; }
    setBusy('discard');
    try { await draftCtl.discard(); onClose(); } catch (e) { setErr(e.message || 'Could not discard the draft.'); setBusy(''); }
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const download = async () => {
    setBusy('download'); setErr('');
    try {
      const blob = await renderPhysicianRequestBlob(data);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = requestFileName(data);
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) { setErr(e.message || 'Could not build the PDF.'); }
    finally { setBusy(''); }
  };

  const fileToDrive = async () => {
    setBusy('drive'); setErr('');
    try {
      const blob = await renderPhysicianRequestBlob(data);
      const fileBase64 = await blobToBase64(blob);
      const filename = requestFileName(data);
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/upload-to-drive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          fileBase64, filename,
          clientId: client.client_id,
          rootFolderId: CLIENT_DRIVE_ROOT,
          subfolder: 'Correspondence',
          contentType: 'application/pdf',
        }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Upload failed');
      if (!client.drive_folder_id && result.clientFolderId) {
        await updateClient(client.id, { drive_folder_id: result.clientFolderId });
      }
      const title = requestTitle(items);
      await addDocument({
        client_id: client.id, client_ref: client.client_id,
        document_name: filename, document_type: 'Correspondence',
        drive_url: result.webViewLink,
        notes: `Physician request (${title}) to ${recipient.name || 'provider'}${recipient.fax ? ` · fax ${recipient.fax}` : ''}`,
        uploaded_by_name: profile.full_name, uploaded_by_id: profile.id,
      });
      await addActivityLog({
        client_id: client.id, client_ref: client.client_id,
        activity_type: 'Document',
        notes: `Physician request generated — ${title} — to ${recipient.name || 'provider'} (${RECIPIENT_KINDS.find(k => k.key === kind)?.label}). Filed under Correspondence.`,
        performed_by_name: profile.full_name, performed_by_id: profile.id,
      });
      onFiled?.(result);
      await draftCtl.discard().catch(() => {});   // filed, so the draft is finished
      onClose();
    } catch (e) { setErr(e.message || 'Could not file the letter.'); }
    finally { setBusy(''); }
  };

  // Same child, same number, within a day: ask before sending it again. Each
  // press is a real fax the office receives and Faxage bills.
  const [dupWarning, setDupWarning] = useState('');
  const send = async () => {
    setBusy('fax'); setErr('');
    try {
      if (!dupWarning) {
        const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
        const { data: recent } = await supabase.from('faxes')
          .select('sent_at, request_title, sent_by_name')
          .eq('direction', 'outbound').eq('client_id', client.id).eq('recipient_fax', faxDigits)
          .gte('sent_at', since).order('sent_at', { ascending: false }).limit(1);
        if (recent?.length) {
          const r = recent[0];
          const when = new Date(r.sent_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
          setDupWarning(`${r.request_title || 'A request'} already went to this number at ${when} today${r.sent_by_name ? ` (${r.sent_by_name})` : ''}. Click Send again to fax it anyway.`);
          setBusy('');
          return;
        }
      }
      const blob = await renderPhysicianRequestBlob(data);
      const letter_base64 = await blobToBase64(blob);
      const title = requestTitle(items);
      const files = items.ppot && blankAppendix && !prefilledAppendix ? [await loadBlankAppendixT()] : [];
      const draftId = await draftCtl.settle();
      const res = await sendFax({
        client_id: client.id, draft_id: draftId || undefined,
        recipient: { name: recipient.name, practice: recipient.practice, fax: faxDigits, kind, directory: chosen ? { type: chosen.type, id: chosen.id, ref_code: chosen.ref_code, role: chosen.role } : undefined },
        items, request_title: title, letter_base64, files,
        attachments: attachedFiles.map(f => ({ drive_file_id: f.id })),
      });
      onSent?.(res);
      onClose();
    } catch (e) { draftCtl.resume(); setErr(e.message || 'The fax could not be sent.'); }
    finally { setBusy(''); }
  };

  const faxDisabledReason = nothingPicked ? 'Pick at least one item.'
    : !recipient.name.trim() ? 'Enter the provider name.'
    : !faxValid ? 'Enter a 10-digit fax number.'
    : !faxConfirmed ? 'Re-enter the fax number to confirm it.'
    : '';

  const label = { fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' };

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && close()}>
      <div className="modal" style={{ maxWidth: isMobile ? 520 : 1080 }} onMouseDown={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Request documents{draft ? <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--warn)', border: '1px solid var(--warn)', borderRadius: 10, padding: '1px 7px', verticalAlign: 'middle' }}>Draft</span> : null}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
              {client.last_name}, {client.first_name} · one letter to a provider, tick what you need
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={close}>✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', gap: 20, flexDirection: isMobile ? 'column' : 'row', padding: 0 }}>
          {/* ── Form ─────────────────────────────────────────────────────── */}
          <div style={{ flex: '0 0 auto', width: isMobile ? '100%' : 400, padding: 20, overflowY: 'auto', borderRight: isMobile ? 'none' : '1px solid var(--border)' }}>
            <span style={label}>Send to</span>
            <div style={{ marginBottom: 12 }}>
              <FaxRecipientPicker client={client} chosen={chosen} onPick={pickRecipient} onClear={clearRecipient} />
              {!chosen && <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 4 }}>Or type the details in below.</div>}
            </div>

            <div className="form-group"><label className="form-label">Provider name</label>
              <input className="form-input" value={recipient.name} onChange={setR('name')} placeholder="Dr. …" /></div>
            <div className="form-group"><label className="form-label">Practice</label>
              <input className="form-input" value={recipient.practice} onChange={setR('practice')} /></div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Fax <span className="req">*</span></label>
                <input className="form-input" value={recipient.fax} onChange={e => { setR('fax')(e); setFaxConfirm(''); }} placeholder="10 digits" style={{ fontFamily: 'monospace' }} /></div>
              <div className="form-group"><label className="form-label">Phone</label>
                <input className="form-input" value={recipient.phone} onChange={setR('phone')} /></div>
            </div>
            {chosen?.recordsFax && items.records && (() => {
              const org = chosen.organisation || 'This organisation';
              const own = digits(chosen.fax) !== digits(chosen.recordsFax) ? chosen.fax : '';
              const link = { background: 'none', border: 'none', padding: 0, color: 'var(--accent)', cursor: 'pointer', font: 'inherit', fontWeight: 600 };
              return (
                <div style={{ fontSize: 11.5, color: 'var(--text2)', background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', borderRadius: 6, padding: '6px 9px', marginTop: -4, marginBottom: 12 }}>
                  {preferOwnFax && own ? (
                    <>Sending to {chosen.name}'s own fax. {org} asks for records requests at {fmtPhone(chosen.recordsFax)}. <button type="button" style={link} onClick={() => setPreferOwnFax(false)}>Use the records fax</button></>
                  ) : (
                    <>{fmtPhone(chosen.recordsFax)} is where {org} takes records requests.{own && (items.ppot || items.lmn) ? <> The PPOT and LMN need {chosen.name}'s signature: <button type="button" style={link} onClick={() => setPreferOwnFax(true)}>send to {fmtPhone(own)} instead</button></> : null}</>
                  )}
                </div>
              );
            })()}
            {faxValid && !faxFromRecord && (
              <div className="form-group">
                <label className="form-label">Confirm fax number <span className="req">*</span></label>
                <input className="form-input" value={faxConfirm} onChange={e => setFaxConfirm(e.target.value)} placeholder="Type it again — this number isn't on the client record or in the directory" style={{ fontFamily: 'monospace' }} />
                {faxConfirm && !faxConfirmed && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>Numbers don't match.</div>}
              </div>
            )}

            <span style={{ ...label, marginTop: 8 }}>Items requested</span>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
              {REQUEST_ITEMS.map((it, i) => (
                <label key={it.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderTop: i ? '1px solid var(--border)' : 'none', cursor: 'pointer', background: items[it.key] ? 'var(--accent-bg)' : 'transparent' }}>
                  <input type="checkbox" checked={!!items[it.key]} onChange={e => setItems(p => ({ ...p, [it.key]: e.target.checked }))} style={{ marginTop: 3 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{i + 1}. {it.label}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
                      {it.key === 'ppot' && 'A blank GAPP Appendix T is enclosed for the physician to complete — or a pre-filled one from Drive, if you attach it below.'}
                      {it.key === 'lmn' && 'Letter on practice letterhead supporting in-home skilled nursing.'}
                      {it.key === 'records' && 'Visit notes, discharge summaries, consults, med list, therapy evals.'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            {nothingPicked && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: -6, marginBottom: 10 }}>Pick at least one item.</div>}

            {items.records && (
              <div className="grid-2">
                <div className="form-group"><label className="form-label">Records from</label>
                  <input className="form-input" type="date" value={form.recordsFrom} onChange={set('recordsFrom')} /></div>
                <div className="form-group"><label className="form-label">Records to</label>
                  <input className="form-input" type="date" value={form.recordsTo} onChange={set('recordsTo')} /></div>
              </div>
            )}
            <span style={{ ...label, marginTop: 4 }}>Hours per week under consideration <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(optional — printed with the request)</span></span>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Skilled nursing (RN/LPN)</label>
                <input className="form-input" value={form.hoursSkilled} onChange={set('hoursSkilled')} placeholder="e.g. 40" inputMode="numeric" /></div>
              <div className="form-group"><label className="form-label">Unskilled (PSS/CNA)</label>
                <input className="form-input" value={form.hoursUnskilled} onChange={set('hoursUnskilled')} placeholder="e.g. 30" inputMode="numeric" /></div>
            </div>

            <span style={{ ...label, marginTop: 4 }}>Enclosures</span>
            {items.ppot && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 8, cursor: prefilledAppendix ? 'default' : 'pointer', opacity: prefilledAppendix ? 0.6 : 1 }}>
                <input type="checkbox" checked={prefilledAppendix ? false : blankAppendix} disabled={prefilledAppendix} onChange={e => setBlankAppendix(e.target.checked)} />
                {prefilledAppendix ? 'Pre-filled Appendix T from Drive is attached (blank form not needed)' : 'Enclose a blank GAPP Appendix T for the physician to complete'}
              </label>
            )}
            <span style={{ fontSize: 11.5, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>From {client.first_name}'s Drive folder</span>
            {faxable.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
                Nothing faxable on file yet — attach the parent's signed release or a pre-filled Appendix T from the Documents tab if you want them to go along.
              </div>
            ) : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 12, maxHeight: 180, overflowY: 'auto' }}>
                {faxable.map((f, i) => (
                  <label key={f.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 12px', borderTop: i ? '1px solid var(--border)' : 'none', cursor: 'pointer', fontSize: 12.5, background: attached.has(f.id) ? 'var(--accent-bg)' : 'transparent' }}>
                    <input type="checkbox" checked={attached.has(f.id)} onChange={() => toggleAttach(f.id)} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }}>{f.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>{[f.bucket, f.path].filter(Boolean).join(' / ')}</span>
                  </label>
                ))}
              </div>
            )}
            {items.ppot && !appendixAttached && (
              <div style={{ fontSize: 12, color: 'var(--warn)', marginTop: -6, marginBottom: 10 }}>No Appendix T enclosed — the letter will ask the office to use their own copy of the form.</div>
            )}

            <div className="grid-2">
              <div className="form-group"><label className="form-label">Purpose</label>
                <select className="form-select" value={form.purpose} onChange={set('purpose')}>
                  <option value="establish">New / initial authorization</option>
                  <option value="renew">Renewal / continuation</option>
                </select></div>
              <div className="form-group"><label className="form-label">Needed by</label>
                <input className="form-input" type="date" value={form.neededBy} onChange={set('neededBy')} /></div>
            </div>

            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.releaseEnclosed} onChange={set('releaseEnclosed')} />
              Parent/guardian signed release is enclosed
            </label>

            <div className="form-group"><label className="form-label">Message to the office <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(optional — printed after the items list)</span></label>
              <textarea className="form-textarea" rows={5} value={form.notes} onChange={set('notes')} placeholder="Anything you want the office to know — e.g. Omari was seen on 8/22; please include that visit. Blank line for a new paragraph." /></div>

            <div className="grid-2">
              <div className="form-group"><label className="form-label">Your title</label>
                <input className="form-input" value={form.senderTitle} onChange={set('senderTitle')} /></div>
              <div className="form-group"><label className="form-label">Direct phone</label>
                <input className="form-input" value={form.senderPhone} onChange={set('senderPhone')} placeholder="Defaults to office line" /></div>
            </div>
          </div>

          {/* ── Preview ──────────────────────────────────────────────────── */}
          <div style={{ flex: 1, minWidth: 0, padding: isMobile ? '0 20px 20px' : '20px 20px 20px 0', position: 'relative', minHeight: isMobile ? 420 : 640 }}>
            {url && (
              <iframe title="Physician request preview" src={`${url}#view=FitH`}
                style={{ width: '100%', height: '100%', minHeight: isMobile ? 400 : 620, border: '1px solid var(--border2)', borderRadius: 8, background: '#fff' }} />
            )}
            {(rendering || previewErr) && (
              <div style={{ position: 'absolute', top: isMobile ? 8 : 28, left: isMobile ? 28 : 8, right: 28, padding: '6px 10px', borderRadius: 6, fontSize: 12,
                background: previewErr ? 'var(--danger-bg)' : 'var(--bg2)', color: previewErr ? 'var(--danger)' : 'var(--text3)', border: '1px solid var(--border)' }}>
                {previewErr ? `Preview failed: ${previewErr}` : 'Rendering letter…'}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 12, color: err ? 'var(--danger)' : 'var(--text3)', flex: 1, minWidth: 200 }}>
            {err || (dupWarning ? <span style={{ color: 'var(--warn)', fontWeight: 600 }}>{dupWarning}</span> : null) || faxDisabledReason || `Sends ${1 + attachedFiles.length + (items.ppot && blankAppendix && !prefilledAppendix ? 1 : 0)} file${attachedFiles.length || (items.ppot && blankAppendix && !prefilledAppendix) ? 's' : ''} to ${recipient.name}. Once delivered, the fax as transmitted is filed under Faxes.`}
            {draftStatusText(draftCtl) && <div style={{ fontSize: 11.5, marginTop: 2, color: draftCtl.status === 'error' ? 'var(--danger)' : 'var(--text3)' }}>{draftStatusText(draftCtl)}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {draftCtl.draftId && <button className="btn btn-ghost" onClick={discard} disabled={!!busy} style={{ color: discardArmed ? 'var(--danger)' : 'var(--text3)' }}>{discardArmed ? 'Click again to discard' : 'Discard draft'}</button>}
            <button className="btn btn-outline" onClick={close} disabled={!!busy}>{draftCtl.draftId ? 'Close' : 'Cancel'}</button>
            <button className="btn btn-outline" onClick={download} disabled={!!busy || nothingPicked}>{busy === 'download' ? 'Building…' : 'Download PDF'}</button>
            <button className="btn btn-outline" onClick={fileToDrive} disabled={!!busy || nothingPicked}>{busy === 'drive' ? 'Filing…' : 'File to Drive'}</button>
            <button className="btn btn-primary" onClick={send} disabled={!!busy || !!faxDisabledReason} title={faxDisabledReason}>{busy === 'fax' ? 'Sending…' : dupWarning ? 'Send again' : 'Send by fax'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
